import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { Envelope, ServerDetail, ServerRankingSort, ServerSummary } from '../types';

/** Position of the server id in `queryKeys.servers.detail(id, page)`. */
const DETAIL_KEY_ID_INDEX = 2;

/** `GET /api/servers` - the public server list. */
export function useServers(): UseQueryResult<ServerSummary[]> {
  return useQuery({
    queryKey: queryKeys.servers.list(),
    queryFn: async () => {
      const res = await api.get<Envelope<ServerSummary[]>>('/servers');
      return res.data;
    },
  });
}

/**
 * `GET /api/servers/{id}` - server detail + one 100-row page of its all-time
 * ranking, by score (default) or by winning time. Page 1 and the score
 * ranking are requested without their params (the plain detail URL).
 */
export function useServer(
  id: number | undefined,
  page = 1,
  sort: ServerRankingSort = 'points',
): UseQueryResult<ServerDetail> {
  return useQuery({
    queryKey: queryKeys.servers.detail(id ?? 0, page, sort),
    enabled: id !== undefined,
    // Paging or re-sorting the ranking keeps the server banner on screen (no
    // flicker) - but only within one server: another server must not wear this
    // one's data. The page renders the ranking the payload says it carries.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[DETAIL_KEY_ID_INDEX] === id ? keepPreviousData(previous) : undefined,
    queryFn: async () => {
      const res = await api.get<Envelope<ServerDetail>>(
        `/servers/${encodeURIComponent(String(id))}`,
        { query: { page: page > 1 ? page : undefined, sort: sort === 'time' ? sort : undefined } },
      );
      return res.data;
    },
  });
}
